import { AxiosError } from "axios";

export interface AxiosErrorWithData {
  response?: { data?: { error: string } };
}

/**
 * Status-code-specific friendly messages for infrastructure-level errors
 * (nginx/proxy responses that typically return HTML bodies).
 * For these status codes the friendly message is returned early, bypassing
 * `response.data`. Callers that need the raw response body for a mapped status
 * code should read `error.response.data` directly.
 */
const STATUS_MESSAGES: Readonly<Record<number, string>> = {
  413: "The file is too large to upload.",
  502: "We're sorry, EVA is unable to communicate with backend services at this moment.",
  503: "We're sorry, EVA is unable to communicate with backend services at this moment.",
  504: "We're sorry, EVA is unable to communicate with backend services at this moment."
};

const looksLikeHtml = (text: string): boolean =>
  /^\s*<(!doctype|html|head|body)/i.test(text);

/**
 * Extracts a user-friendly error message from an AxiosError.
 *
 * Checks the following in order:
 * 1. Status-code-specific friendly message (e.g., 413 → "The file is too large")
 * 2. `response.data` if it's a plain non-HTML string
 * 3. `response.data.error` if the backend returns `{ error: "..." }`
 * 4. `response.data.message` if the backend returns `{ message: "..." }`
 * 5. `error.message` (Axios default, e.g., "Network Error")
 * 6. Fallback to a generic message
 */
export const extractAxiosErrorMessage = (
  error: AxiosError<{ error?: string; message?: string } | string>,
  fallback = "An unexpected error occurred."
): string => {
  const status = error.response?.status;
  if (status && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }

  const data = error.response?.data;

  if (typeof data === "string" && data.length > 0 && !looksLikeHtml(data)) {
    return data;
  }

  if (typeof data === "object" && data !== null) {
    if (data.error) return data.error;
    if (data.message) return data.message;
  }

  return error.message || fallback;
};
